/**
 * Static content for the custom WLE landing page.
 *
 * This is the single source of truth for the page copy AND the structured data
 * (JSON-LD) emitted for SEO. The FAQ list, for example, both renders the on-page
 * accordion and generates the FAQPage schema, so the two can never drift apart.
 *
 * Ported from the Claude Design handoff (WLE - Home Page). The handoff's saved
 * variant was: centered hero, "experts" headline, provider-first CTA emphasis.
 */

// Booth / service categories shown in the "In demand right now" grid.
export const BOOTH_TYPES = [
  { icon: 'Bot', label: 'Camera Robots' },
  { icon: 'Disc3', label: 'DJ / MC Service' },
  { icon: 'Box', label: 'Enclosed Booths' },
  { icon: 'PartyPopper', label: 'Event Services' },
  { icon: 'Aperture', label: 'iPad + Camera Booths' },
  { icon: 'Tablet', label: 'iPad Booths' },
  { icon: 'Monitor', label: 'PC + Camera Booths' },
  { icon: 'Package', label: 'Photo Booth Accessories' },
  { icon: 'Move', label: 'Roaming Booths' },
  { icon: 'Camera', label: 'Roaming Photography' },
  { icon: 'WandSparkles', label: 'Specialty Photo Booth Services' },
];

// Service-type cards. Static content (no links): the per-type landing pages
// don't exist yet, so these describe the subcontracted booth/service types
// without implying a destination. B2B framing — the formats a hiring company
// can subcontract, not consumer event examples.
export const EVENT_TYPES = [
  {
    icon: 'Camera',
    title: 'Open-air booths',
    blurb: 'DSLR open-air setups, the most-requested subcontract across the network.',
  },
  {
    icon: 'Rotate3d',
    title: '360 booths',
    blurb: 'Slow-mo video platforms for high-energy activations and dance floors.',
  },
  {
    icon: 'Square',
    title: 'Mirror booths',
    blurb: 'Interactive full-length mirror booths with animated touch overlays.',
  },
  {
    icon: 'Sparkles',
    title: 'Glam & B&W booths',
    blurb: 'Glam skin-smoothing and black-and-white setups for premium activations.',
  },
  {
    icon: 'Move',
    title: 'Roaming booths',
    blurb: 'Untethered roaming photo and video capture that works the whole room.',
  },
  {
    icon: 'Bot',
    title: 'AI booths',
    blurb: 'AI-generated portraits and themed shoots for brand activations.',
  },
  {
    icon: 'Truck',
    title: 'Photo bus',
    blurb: 'Vintage photo-bus and trailer setups for large-footprint events.',
  },
  {
    icon: 'Box',
    title: 'Enclosed booths',
    blurb: 'Classic curtained kiosks with printed strips, the original photo booth.',
  },
];

// State -> cities coverage. Static content (no links): the per-city landing
// pages don't exist yet, so these show coverage without implying a destination.
export const STATES = [
  {
    name: 'California',
    cities: ['Los Angeles', 'San Diego', 'San Francisco', 'Sacramento', 'San Jose', 'Oakland'],
  },
  {
    name: 'New York',
    cities: ['New York City', 'Brooklyn', 'Long Island', 'Buffalo', 'Rochester', 'Albany'],
  },
  {
    name: 'Florida',
    cities: ['Miami', 'Orlando', 'Tampa', 'Fort Lauderdale', 'Jacksonville', 'West Palm Beach'],
  },
];

// FAQ list. Drives both the on-page accordion and the FAQPage JSON-LD.
export const FAQ_ITEMS = [
  {
    q: 'How are subcontractor rates set?',
    a:
      'Providers set their own bid on each RFP — there is no fixed price list. Rates vary by booth type, market, and event scope. Post a free RFP to see live bids from providers in your area, usually within 24 hours.',
  },
  {
    q: 'How far in advance should I book a photo booth?',
    a:
      'Book 4 to 6 weeks ahead for weekday corporate events and 8 to 12 weeks for Saturday weddings. On WLE you will usually see your first bid within 24 hours of posting your RFP, and most events are booked within 7 days.',
  },
  {
    q: 'Is White Label Experts free for hiring companies?',
    a:
      'Yes. Posting an RFP, comparing bids, and messaging providers is 100% free for hiring companies. You only pay the provider at the price you agree on. There are no platform fees, no subscriptions, no markup.',
  },
  {
    q: 'How do photo booth providers get paid?',
    a:
      'Payments are processed through Stripe. Funds are held until the event is completed, then released to the provider. WLE charges a flat 10% commission only on winning bids, when the booking is confirmed and the retainer is paid.',
  },
  {
    q: 'Where is White Label Experts available?',
    a:
      'WLE operates across the United States and Canada. You can post an RFP for any city we cover, and we will match it to providers whose service radius includes the event location.',
  },
  {
    q: 'How do I know a provider is reliable?',
    a:
      'Providers confirm they carry liability insurance, and every booking runs on a verified two-way review system — you can see a provider\'s star rating, completed events, and sample work before you accept a bid. Payments are held in escrow with dispute resolution until the event is complete, so your money is protected.',
  },
  {
    q: "What's the difference between 360, mirror, open-air, and enclosed booths?",
    a:
      '360 booths capture slow-mo video on a rotating arm, best for dance floors. Mirror booths are interactive full-length mirrors with animated overlays. Open-air booths use a DSLR plus backdrop and fit large groups. Enclosed booths are classic curtained kiosks with printed strips.',
  },
  {
    q: 'Can I cancel or reschedule after booking?',
    a:
      'Yes. Each provider sets their own cancellation policy, which is shown clearly inside their bid before you accept. Most allow a full refund up to 30 days out and a 50% refund within 14 days.',
  },
  {
    q: 'How do I become a Provider?',
    a:
      'Create a free login and complete your profile with your location, services, and experience. Once your business is set up, you will onboard to Stripe and can start bidding on matching RFPs right away.',
  },
  {
    q: 'How do RFPs reach providers, and is bidding free?',
    a:
      'It is a two-sided marketplace where every member can post RFPs and bid on others. Our algorithm automatically routes each RFP to eligible providers by service type and location, and matched providers get a notification email. Getting listed and submitting bids is completely free.',
  },
  {
    q: 'When does WLE charge commission?',
    a:
      'Never on browsing or bidding. Commission is charged only on winning bids, when the booking is confirmed and the retainer payment is made. No subscriptions, no listing fees.',
  },
  {
    q: 'Who handles pre-event coordination and artwork?',
    a:
      'The hiring company that posted the event stays responsible for all coordination and artwork creation, even when a provider is subcontracted. Day-of logistics, design assets, and client communication remain with the hiring company, while the provider delivers the booth and on-site service.',
  },
  {
    q: 'How do reviews and ratings work?',
    a:
      'After every event, both members rate each other on performance, reliability, and overall experience. Verified ratings travel with each future RFP and bid, so the best jobs go to the highest-rated partners on both sides.',
  },
];

/**
 * Build the schema.org structured data for the landing page.
 *
 * Note: Organization and WebSite schemas are added automatically by
 * src/components/Page/Page.js, so we only add page-specific entries here
 * (Service, BreadcrumbList, FAQPage).
 *
 * We deliberately do NOT emit AggregateRating / Review schema: the design
 * handoff carried fabricated ratings and named reviews, which violate Google's
 * structured-data policy and risk a manual action. Wire real review data here
 * later if/when it exists.
 *
 * @param {string} rootUrl marketplace root URL (no trailing slash guaranteed by caller)
 */
export const buildLandingSchema = rootUrl => {
  const base = (rootUrl || '').replace(/\/$/, '');
  return [
    {
      '@context': 'http://schema.org',
      '@type': 'Service',
      name: 'Photo Booth Rental Marketplace',
      serviceType: 'Photo booth rental',
      areaServed: ['United States', 'Canada'],
      provider: { '@type': 'Organization', name: 'White Label Experts', url: `${base}/` },
      hasOfferCatalog: {
        '@type': 'OfferCatalog',
        name: 'Photo booth categories',
        itemListElement: [
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: '360 photo booth rental' } },
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Mirror photo booth rental' } },
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Open air photo booth rental' } },
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Enclosed photo booth rental' } },
        ],
      },
    },
    {
      '@context': 'http://schema.org',
      '@type': 'FAQPage',
      mainEntity: FAQ_ITEMS.map(item => ({
        '@type': 'Question',
        name: item.q,
        acceptedAnswer: { '@type': 'Answer', text: item.a },
      })),
    },
  ];
};
